import { useState } from 'react';
import { Link, generatePath, useNavigate } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import { allServicesMsg, companyMsg, idNumberMsg, orderMsg, statusMsg, t } from '../../common/i18n';
import { getCompanyName, isMultiBiz } from '../../common/utils/accountUtils';
import { Contract, SubscriptionType } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider';
import { SelectCompanyDialog } from '../Dialogs/SelectCompanyDialog';
import { STATUS_COLORS, getContractStatus } from '../MobileId/MobileIdDetails.js';
import { MobileIdDisabled } from '../MobileId/MobileIdDisabled.js';
import { Status } from '../Status/Status.js';
import { DEFAULT_ITEMS_PER_PAGE, ListPagination, ListSearch, Table, TableTopActions, tableSearch } from '../Table';
import { setDefaultItemsPerPage } from '../Table/tableUtils.js';
import { getSubscriptionIcon } from './subscriptionIcons.js';
import { getPaginatedItems } from './subscriptionListUtils.js';
import type { CLTableRow } from '../../common/types/componentLibrary';
import type { AuthenticatedUser } from '../../public/site/authState';
import type { DefaultListSearchParams, TableTopButton } from '../Table';

interface ServiceContractsProps {
  isMobileIdEnabled?: boolean;
  mobileIdContracts?: Contract[];
  offset: number;
  search?: string;
  total?: number;
  itemsPerPage?: number;
}

const getContractRows = (mobileIdContracts: Contract[] = [], authenticatedUser?: AuthenticatedUser): CLTableRow[] =>
  mobileIdContracts
    .filter(
      contract =>
        contract.contractStatus === Contract.ContractStatusEnum.ACTIVATED ||
        contract.contractStatus === Contract.ContractStatusEnum.TERMINATED
    )
    .map(contract => ({
      icon: (
        <div
          className={`of-subscription-table__icon-column of-icon--${getSubscriptionIcon(
            SubscriptionType.MOBILE_CERTIFICATE
          )}`}
        ></div>
      ),
      subscriptionName: (
        <Link
          to={generatePath(paths.PS_CONTRACT_MOBILE_ID, { contractDisplayId: contract.contractNumber })}
          className="of-subscription-table__text-column"
        >
          {contract.contractName}
        </Link>
      ),
      subscriptionStatus: (
        <Status
          color={STATUS_COLORS[contract.contractStatus]}
          textAfterBadge={getContractStatus()[contract.contractStatus]}
          inTable={true}
        />
      ),
      subscriptionUserFriendlyId: contract.contractNumber,
      company: getCompanyName(authenticatedUser, contract.accountMasterId),
    }));

export const ServiceContracts = ({
  isMobileIdEnabled,
  mobileIdContracts,
  offset,
  search,
  itemsPerPage,
}: ServiceContractsProps) => {
  const navigate = useNavigate();
  const searchParams = useSearchParams<DefaultListSearchParams>();
  const { authenticatedUser } = useAuth();
  const [showSelectCompanyDialog, setShowSelectCompanyDialog] = useState(false);

  if (!isMobileIdEnabled) {
    return <MobileIdDisabled />;
  }

  const tableTopButtons: TableTopButton[] = [
    {
      label: t.AQOL(orderMsg),
      onClick: () => {
        if (isMultiBiz(authenticatedUser)) {
          setShowSelectCompanyDialog(true);
        } else {
          navigate(paths.PS_CONTRACT_MOBILE_ID_NEW);
        }
      },
    },
  ];

  const contractColumns = [
    { key: 'icon', label: '' },
    { key: 'subscriptionName', label: t.NX0T(allServicesMsg) },
    { key: 'subscriptionUserFriendlyId', label: t.YTH3(idNumberMsg), sortable: false },
    { key: 'subscriptionStatus', label: t.ASQT(statusMsg), sortable: false },
    { key: 'company', label: t.KJTS(companyMsg), sortable: false },
  ];

  const contractRows = getContractRows(mobileIdContracts, authenticatedUser);
  const rows = search ? tableSearch(search, contractRows) : contractRows;

  return (
    <div className="of-subscription-table">
      {showSelectCompanyDialog && (
        <SelectCompanyDialog
          onCloseDialog={() => setShowSelectCompanyDialog(false)}
          onContinue={selectedMdmId => {
            navigate(`${paths.PS_CONTRACT_MOBILE_ID_NEW}?mdmId=${selectedMdmId}`);
          }}
        />
      )}
      <TableTopActions buttons={tableTopButtons} />
      <ListSearch onSearch={() => setDefaultItemsPerPage(new URLSearchParams(searchParams), DEFAULT_ITEMS_PER_PAGE)} />
      <Table
        columns={contractColumns}
        noItemsText={t.XZA8('No services')}
        rows={getPaginatedItems(itemsPerPage, offset, rows)}
      />
      <ListPagination totalItems={rows.length} />
    </div>
  );
};
